import { Clientize, ProgramType, WelcomeCallProgramConfig, WelcomeCallStipRulesValue } from 'types';

import { api } from './api';

export interface WelcomeCallStipRulesHistoryDiff {
  path: string;
  from: unknown;
  to: unknown;
}

export interface WelcomeCallStipRulesHistoryEntry {
  timestamp: string;
  actorEmail?: string;
  diffs: WelcomeCallStipRulesHistoryDiff[];
}

export interface WelcomeCallStipRulesHistoryResponse {
  data: WelcomeCallStipRulesHistoryEntry[];
  total: number;
}

export interface UpdateWelcomeCallStipRulesParams {
  programType: ProgramType;
  templateId: string;
  value: WelcomeCallStipRulesValue;
}

interface GetWelcomeCallStipRulesHistoryParams {
  programType?: ProgramType;
  limit?: number;
  offset?: number;
}

export const welcomeCallStipRulesApi = api.injectEndpoints({
  endpoints: (build) => ({
    getWelcomeCallStipRules: build.query<Clientize<WelcomeCallProgramConfig>, ProgramType>({
      query: (programType) => ({
        url: `/welcome-call-stip-rules?programType=${programType}`,
      }),
      providesTags: [{ type: 'WelcomeCallStipRules', id: 'CURRENT' }],
    }),
    updateWelcomeCallStipRules: build.mutation<
      Clientize<WelcomeCallProgramConfig>,
      UpdateWelcomeCallStipRulesParams
    >({
      query: (body) => ({
        url: '/welcome-call-stip-rules',
        method: 'PUT',
        body,
      }),
      invalidatesTags: [
        { type: 'WelcomeCallStipRules', id: 'CURRENT' },
        { type: 'WelcomeCallStipRules', id: 'HISTORY' },
      ],
    }),
    updateWelcomeCallEnabled: build.mutation<
      { programType: ProgramType; enabled: boolean },
      { programType: ProgramType; enabled: boolean }
    >({
      query: (body) => ({ url: '/welcome-call-stip-rules/enabled', method: 'PATCH', body }),
      invalidatesTags: [{ type: 'WelcomeCallStipRules', id: 'CURRENT' }],
    }),
    getWelcomeCallStipRulesHistory: build.query<
      WelcomeCallStipRulesHistoryResponse,
      GetWelcomeCallStipRulesHistoryParams | void
    >({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.programType) searchParams.set('programType', params.programType);
        if (params?.limit !== undefined) searchParams.set('limit', String(params.limit));
        if (params?.offset !== undefined) searchParams.set('offset', String(params.offset));
        const qs = searchParams.toString();
        return { url: `/welcome-call-stip-rules/history${qs ? `?${qs}` : ''}` };
      },
      providesTags: [{ type: 'WelcomeCallStipRules', id: 'HISTORY' }],
    }),
  }),
});

export const {
  useGetWelcomeCallStipRulesQuery,
  useUpdateWelcomeCallStipRulesMutation,
  useUpdateWelcomeCallEnabledMutation,
  useGetWelcomeCallStipRulesHistoryQuery,
} = welcomeCallStipRulesApi;
